import React, { useState, useEffect } from 'react';
import { FileText, Download, Eye, Loader2 } from 'lucide-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { formatDate as formatDateUtil } from '../../../utils/dateUtils';

interface Invoice {
  id: string;
  invoiceNumber: string;
  date: string;
  amount: number;
  status: 'paid' | 'pending' | 'failed';
  description: string;
  downloadUrl?: string;
  hostedUrl?: string;
}

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const AccountInvoices: React.FC = () => {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/stripe/invoices`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setInvoices(data.data);
      }
    } catch (error) {
      console.error('Failed to load invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: Invoice['status']) => {
    const styles = {
      paid: 'bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300',
      pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
      failed: 'bg-error-100 dark:bg-error-900/30 text-error-800 dark:text-error-300',
    };

    const labels = {
      paid: t('admin.paid'),
      pending: t('admin.pending'),
      failed: t('admin.failed'),
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const handleDownload = (invoice: Invoice) => {
    if (invoice.downloadUrl) {
      window.open(invoice.downloadUrl, '_blank');
    }
  };

  const handleView = (invoice: Invoice) => {
    if (invoice.hostedUrl) {
      window.open(invoice.hostedUrl, '_blank');
    }
  };

  const formatDate = (dateString: string) => {
    return formatDateUtil(dateString);
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 text-primary-500 dark:text-primary-400 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('admin.billingHistory')}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('admin.billingHistoryDescription')}
        </p>
      </div>

      {/* Invoices List */}
      {invoices.length === 0 ? (
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-12 border border-gray-200 dark:border-gray-600">
          <div className="flex flex-col items-center justify-center text-center">
            <FileText className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {t('admin.noInvoicesYet')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('admin.noInvoicesDescription')}
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('admin.invoice')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('admin.date')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('admin.description')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('admin.amount')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('admin.status')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FileText className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-3" />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {invoice.invoiceNumber}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {formatDate(invoice.date)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                      {invoice.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                      {formatAmount(invoice.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(invoice.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end space-x-2">
                        {invoice.hostedUrl && (
                          <button
                            onClick={() => handleView(invoice)}
                            className="p-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                            title={t('admin.viewInvoice')}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        {invoice.downloadUrl && (
                          <button
                            onClick={() => handleDownload(invoice)}
                            className="p-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                            title={t('admin.downloadPdf')}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-600">
            {invoices.map((invoice) => (
              <div key={invoice.id} className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center">
                    <FileText className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {invoice.invoiceNumber}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {formatDate(invoice.date)}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(invoice.status)}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {invoice.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {formatAmount(invoice.amount)}
                  </span>
                  <div className="flex space-x-2">
                    {invoice.hostedUrl && (
                      <button
                        onClick={() => handleView(invoice)}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                        title={t('admin.viewInvoice')}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    {invoice.downloadUrl && (
                      <button
                        onClick={() => handleDownload(invoice)}
                        className="p-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                        title={t('admin.downloadPdf')}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Billing Info */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          <strong>{t('admin.needHelpWithBilling')}</strong> {t('admin.contactSupport')}{' '}
          <a href="mailto:billing@yourapp.com" className="text-primary-600 dark:text-primary-400 hover:underline">
            billing@yourapp.com
          </a>
        </p>
      </div>
    </div>
  );
};
